import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Col,
  Container,
  Form,
  Row,
  Stack
} from "react-bootstrap";
import { useParams } from "react-router-dom";
import { useNotification } from "../../../../hooks/NotificationContext";
import { generateNotificationUrl } from "../../../../lib/notificationUtils";
import { formatCommentWithAccountIds } from "../../../../lib/utils";
import { addComment, getComments } from "../../../../modules/entity-details";
import DetailsCard from "../../../../ui/layout/DetailsCard";
import CustomAvatar from "../../CustomAvatar";
import SkeletonLoader from "../../SkeletonLoader";
import CommentInput from "./CommentInput";
import SingleComment from "./SingleComment";

const Comments = ({
  entityType,
  entityId,
  users,
  isEditable,
  entityKey,
  otherData,
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState("");
  const [error, setError] = useState(null);
  const { addNotification } = useNotification();
  const [sort, setSort] = useState("Newest First");
  const [taggedUsers, setTaggedUsers] = useState([]);
  const currentUser = JSON.parse(localStorage.getItem("user"));
  const { projectId } = useParams();
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getComments(entityType, entityId, projectId);
      setData(response.data);
      setError(null);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const createComment = async () => {
    const mentions = [...new Set(taggedUsers)];
    const commentBody = formatCommentWithAccountIds(comment, users);
    const notificationLink = generateNotificationUrl(
      entityType,
      projectId,
      entityKey,
      "comments",
      otherData
    );
    let formData = {
      comment: {
        body: commentBody.trim(),
        entityType: entityType,
        entityId: entityId,
        mentions: mentions,
        additionalData: {
          commentorName: currentUser?.first_name + " " + currentUser?.last_name,
          entityKey,
          entityLink: notificationLink,
        },
      },
    };
    try {
      await addComment(formData, projectId);
      await fetchData();
      addNotification("success", "Comment added successfully");
      setComment("");
      setTaggedUsers([]);
      fetchData();
    } catch (error) {
      const errorMessage =
        error.message || "Failed to add comment. Please try again";
      addNotification("error", errorMessage);
    }
  };

  useEffect(() => {
    if (entityId) {
      fetchData();
    }
    return () => {
      setData([]);
    };
  }, [entityId]);

  const getDisplayName = (user) => {
    return user;
  };

  const sortedData = useCallback(() => {
    if (sort === "Newest First") {
      return [...data].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
    } else if (sort === "Oldest First") {
      return [...data].sort(
        (a, b) => new Date(a.created_at) - new Date(b.created_at)
      );
    }
  }, [data, sort]);

  const handleFilter = (e) => {
    setSort(e.target.value);
  };

  const finalData = sortedData();

  const cardActions = finalData.length > 1 && (
    <Form.Select
      style={{ width: "140px" }}
      aria-label="Sort By: "
      onChange={handleFilter}
    >
      <option>Newest First</option>
      <option>Oldest First</option>
    </Form.Select>
  );


  return (
    <DetailsCard
      title="Comments"
      actions={cardActions}
    >

      <Container fluid>
        {finalData.length === 0 && (
          <Row>
            <Col className="text-center">
              {loading ? (
                <SkeletonLoader rowCount={2} />
              ) : (
                "No comment added yet"
              )}
            </Col>
            <hr style={{ margin: "1rem 0" }} />
          </Row>
        )}
        {finalData.length > 0 && (
          <Stack gap={3}>
            {finalData.map((com) => (
              <SingleComment
                key={com.id}
                comment={com}
                name={getDisplayName(com.jira_account_id)}
                fetchData={fetchData}
                addNotification={addNotification}
                isEditable={isEditable}
                users={users}
                entityKey={entityKey}
                otherData={otherData}
              />
            ))}
          </Stack>
        )}
      </Container>
      <Container fluid>
        <Row className="mt-3">
          <Col>
            <Form>
              <Row>
                <Col xs="auto">
                  <CustomAvatar
                    firstName={currentUser?.first_name}
                    lastName={currentUser?.last_name}
                  />
                </Col>
                <Col className="mb-2">
                  <CommentInput
                    users={users}
                    comment={comment}
                    onCommentChange={setComment}
                    error={error}
                    setError={setError}
                    setTaggedUsers={setTaggedUsers}
                  />
                </Col>
              </Row>
              {comment && (
                <Row>
                  <Col className="text-end">
                    <Button
                      className="mx-2 px-3"
                      onClick={createComment}
                      size="sm"
                      disabled={!comment}
                      title={!comment ? "Please enter comment" : ""}
                      variant="primary"
                    >
                      Add
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => setComment("")}
                      variant="outline-secondary"
                    >
                      Cancel
                    </Button>
                  </Col>
                </Row>
              )}
            </Form>
          </Col>
        </Row>
      </Container>
    </DetailsCard >
  );
};

export default Comments;
